import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../components/ui/PageHeader'
import { Spinner } from '../components/ui/Spinner'
import { organisationAdminService } from '../services/organisationAdminService'

function formatDateTime(isoOrDate) {
  if (!isoOrDate) return '—'
  const d = new Date(isoOrDate)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function workflowTitle(workflow) {
  if (!workflow) return '—'
  if (workflow.source === 'PRODUCT') {
    return workflow.product_config_name || workflow.display_company_name || 'Product discovery'
  }
  return workflow.workflow_name || workflow.display_company_name || workflow.company_name || 'ICP workflow'
}

function listPreview(value, max = 2) {
  if (!Array.isArray(value) || value.length === 0) return '—'
  const shown = value.slice(0, max).join(', ')
  if (value.length > max) return `${shown} +${value.length - max}`
  return shown
}

const PAGE_SIZE = 25

const defaultPagination = {
  current_page: 1,
  per_page: PAGE_SIZE,
  total_count: 0,
  total_pages: 0,
  has_next_page: false,
  has_prev_page: false,
}

const OrganisationWorkflowLeads = () => {
  const { organisationId, workflowId } = useParams()
  const [workflow, setWorkflow] = useState(null)
  const [leads, setLeads] = useState([])
  const [pagination, setPagination] = useState(defaultPagination)
  const [page, setPage] = useState(1)
  const [searchDraft, setSearchDraft] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [expandedLeadId, setExpandedLeadId] = useState(null)
  const [personsByLead, setPersonsByLead] = useState({})
  const [personsLoadingId, setPersonsLoadingId] = useState(null)

  const fetchWorkflowAndLeads = useCallback(async () => {
    setLoading(true)
    setNotFound(false)
    try {
      const [workflowRes, leadsRes] = await Promise.all([
        organisationAdminService.getOrganisationWorkflow(organisationId, workflowId),
        organisationAdminService.listOrganisationWorkflowLeads(organisationId, workflowId, {
          page,
          limit: PAGE_SIZE,
          search: appliedSearch,
        }),
      ])
      const wf = workflowRes?.data ?? null
      if (!wf) {
        setNotFound(true)
        return
      }
      setWorkflow(wf)
      const payload = leadsRes?.data
      setLeads(Array.isArray(payload?.leads) ? payload.leads : [])
      setPagination(payload?.pagination ?? defaultPagination)
    } catch {
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }, [organisationId, workflowId, page, appliedSearch])

  useEffect(() => {
    fetchWorkflowAndLeads()
  }, [fetchWorkflowAndLeads])

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    setPage(1)
    setAppliedSearch(searchDraft.trim())
  }

  const handleClearSearch = () => {
    setSearchDraft('')
    setAppliedSearch('')
    setPage(1)
  }

  const toggleLead = async (leadId) => {
    if (expandedLeadId === leadId) {
      setExpandedLeadId(null)
      return
    }
    setExpandedLeadId(leadId)
    if (personsByLead[leadId]) return
    setPersonsLoadingId(leadId)
    try {
      const res = await organisationAdminService.getOrganisationLeadPersons(organisationId, leadId)
      const persons = Array.isArray(res?.data?.persons) ? res.data.persons : []
      setPersonsByLead((prev) => ({ ...prev, [leadId]: persons }))
    } catch {
      setPersonsByLead((prev) => ({ ...prev, [leadId]: [] }))
    } finally {
      setPersonsLoadingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    )
  }

  if (notFound || !workflow) {
    return (
      <div>
        <PageHeader
          title="Workflow not found"
          breadcrumbs={[
            { label: 'Organisations', href: '/admin/organisations' },
            { label: 'Organisation', href: `/admin/organisations/${organisationId}` },
            { label: 'Not found' },
          ]}
        />
        <div className="card text-text-muted">This workflow could not be loaded.</div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title={workflowTitle(workflow)}
        subtitle={`${workflow.source === 'PRODUCT' ? 'Product discovery' : 'ICP workflow'} · ${workflow.status || '—'} · Started ${formatDateTime(workflow.started_at)}${workflow.actual_leads_count != null ? ` · ${workflow.actual_leads_count} leads` : ''}`}
        breadcrumbs={[
          { label: 'Organisations', href: '/admin/organisations' },
          { label: 'Organisation', href: `/admin/organisations/${organisationId}` },
          { label: workflowTitle(workflow) },
        ]}
      />

      <div className="card mb-8">
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 text-sm">
          <div>
            <dt className="font-medium text-gray-500">Workflow ID</dt>
            <dd className="mt-1 font-mono text-gray-900 break-all">{workflow.workflow_id}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Company</dt>
            <dd className="mt-1 text-gray-900">{workflow.display_company_name || workflow.company_name || '—'}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Website</dt>
            <dd className="mt-1 text-gray-900">
              {workflow.display_company_website || workflow.company_website ? (
                <a
                  href={
                    (workflow.display_company_website || workflow.company_website).startsWith('http')
                      ? workflow.display_company_website || workflow.company_website
                      : `https://${workflow.display_company_website || workflow.company_website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 hover:underline break-all"
                >
                  {workflow.display_company_website || workflow.company_website}
                </a>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">Finished</dt>
            <dd className="mt-1 text-gray-900">{formatDateTime(workflow.finished_at)}</dd>
          </div>
        </dl>
      </div>

      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Leads ({pagination.total_count || 0})</h2>
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <input
              type="search"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Search company, website, emails…"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-64"
            />
            <button
              type="submit"
              className="rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
            >
              Search
            </button>
            {appliedSearch ? (
              <button type="button" onClick={handleClearSearch} className="rounded-lg border px-3 py-2 text-sm">
                Clear
              </button>
            ) : null}
          </form>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[960px] w-full divide-y divide-gray-200 text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Company
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Website
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Tier
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Scores
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Contacts
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {' '}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leads.map((lead) => {
                const expanded = expandedLeadId === lead.lead_id
                const persons = personsByLead[lead.lead_id]
                return (
                  <React.Fragment key={lead.lead_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{lead.company_name || '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        {lead.website ? (
                          <a
                            href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-800 hover:underline break-all"
                          >
                            {lead.website}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{lead.tier || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {lead.source === 'PRODUCT'
                          ? `Fit ${lead.icp_score ?? '—'} · Intent ${lead.receptivity_score ?? '—'} · Total ${lead.total_score ?? '—'}`
                          : `ICP ${lead.icp_score ?? '—'} · Rec ${lead.receptivity_score ?? '—'} · Total ${lead.total_score ?? '—'}`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {listPreview(lead.emails)}
                        {Array.isArray(lead.phone_numbers) && lead.phone_numbers.length > 0
                          ? ` · ${listPreview(lead.phone_numbers)}`
                          : ''}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          type="button"
                          onClick={() => toggleLead(lead.lead_id)}
                          className="font-medium text-purple-600 hover:text-purple-800"
                        >
                          {expanded ? 'Hide people' : 'People'}
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={6} className="bg-gray-50 px-6 py-4">
                          {personsLoadingId === lead.lead_id ? (
                            <p className="text-sm text-gray-500">Loading people…</p>
                          ) : !persons || persons.length === 0 ? (
                            <p className="text-sm text-gray-500">No people on this lead.</p>
                          ) : (
                            <table className="min-w-full text-left text-sm">
                              <thead>
                                <tr className="text-xs uppercase text-gray-500">
                                  <th className="py-1 pr-4">Name</th>
                                  <th className="py-1 pr-4">Designation</th>
                                  <th className="py-1 pr-4">Email</th>
                                  <th className="py-1 pr-4">Phone</th>
                                  <th className="py-1">LinkedIn</th>
                                </tr>
                              </thead>
                              <tbody>
                                {persons.map((person) => (
                                  <tr key={person.lead_person_id}>
                                    <td className="py-1 pr-4 text-gray-900">{person.person_name || '—'}</td>
                                    <td className="py-1 pr-4 text-gray-700">{person.designation || '—'}</td>
                                    <td className="py-1 pr-4 text-gray-700">{person.email || '—'}</td>
                                    <td className="py-1 pr-4 text-gray-700">{person.phone_number || '—'}</td>
                                    <td className="py-1">
                                      {person.linkedin_url ? (
                                        <a
                                          href={person.linkedin_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-purple-600 hover:underline"
                                        >
                                          Profile
                                        </a>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
          {leads.length === 0 && (
            <div className="px-6 py-10 text-center text-gray-500 text-sm">No leads generated for this workflow.</div>
          )}
        </div>
        {pagination.total_pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between text-sm">
            <button
              type="button"
              disabled={!pagination.has_prev_page}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="text-purple-600 disabled:text-gray-400"
            >
              Previous
            </button>
            <span className="text-gray-600">
              Page {pagination.current_page} of {pagination.total_pages}
            </span>
            <button
              type="button"
              disabled={!pagination.has_next_page}
              onClick={() => setPage((p) => p + 1)}
              className="text-purple-600 disabled:text-gray-400"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrganisationWorkflowLeads
